// @flow
import React, {Component} from 'react';
import {Link} from '@reach/router';
import styled from 'styled-components';
import moment from 'moment';
import qs from 'query-string';

import {Button, DatePicker, Popconfirm} from 'antd';
import notification from 'antd/lib/notification';

import type {FetchListParams} from '../../../lib/api';
import {osagoMultiplier} from '../../../lib/api';
import {
  formatDateTimeToISOString,
  formatDateTimeToString,
  getListInitialState,
  navigate,
  setQueryParams,
} from '../../../lib/helpers';
import type {ListState, OsagoGeneralMultiplier, UserAccess} from './../../../lib/types';

import {Header, Section} from '../../../components/layout';
import {notificationLoading} from '../../../components/Notifications';
import {Icon, Operations, Table} from '../../../components/ui';
import {HeaderTabs} from '../components';

import {withUserAccess} from './../../withUserAccess';
import canAddOsagoMultiplier from './../accessRight';

import Filter, {type GenerelFilterParams} from './Filter';

type Props = {
  location: Location & { state: { page: number } },
  userAccess: UserAccess[]
};
type State = ListState<OsagoGeneralMultiplier> & {
  // Хранится значение для проверки пересечения до создания документа
  startDate: moment,
  filter: GenerelFilterParams
};

const StyledIcon = styled(Icon)`
  margin: 0 5px;
  color: #1890ff;
  cursor: pointer;
`;
class OsagoGeneralMultiplierForm extends Component<Props, State> {
  state = {
    ...getListInitialState(),
    startDate: moment.utc(new Date()).startOf('day'),
    filter: {}
  };

  fetchOsagoGeneralMultiplier = async (
    page: number = 1,
    params: FetchListParams<> = {}
  ) => {
    this.setState({ loading: true });
    const {
      data,
      totalCount
    } = await osagoMultiplier.fetchOsagoGeneralMultiplier({
      page,
      ...params,
      // $FlowFixMe
      ...this.state.filter
    });
    setQueryParams({ page });
    this.setState({ loading: false, data, totalCount, page });
  };

  async componentDidMount() {
    try {
      const { page, ...filter } = qs.parse(window.location.search);

      this.setState(
        {
          filter: { ...filter },
          page
        },
        () => this.fetchOsagoGeneralMultiplier(page)
      );

      if (this.canAdd()) {
        this.columns.push({
          title: '',
          align: 'right',
          stopPropagation: true,
          width: 50,
          render: record => (
            <Operations>
              <Popconfirm
                overlayStyle={{
                  zIndex: 2000
                }}
                placement="left"
                title="Вы действительно хотите удалить?"
                okText="Да"
                cancelText="Нет"
                onConfirm={async () =>
                  await this.deleteGeneralMultiplier(record.id)
                }
              >
                <StyledIcon type="x" />
              </Popconfirm>
            </Operations>
          )
        });
      }
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: error.message
      });
    }
  }

  deleteGeneralMultiplier = async (id: number) => {
    try {
      notificationLoading({
        message: 'Удаление...',
        key: 'deleting'
      });
      await osagoMultiplier.deleteOsagoGeneralMultiplier(id);
      await this.fetchOsagoGeneralMultiplier(this.state.page);
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: error.message
      });
    } finally {
      notification.close('deleting');
    }
  };

  columns: Array<Object> = [
    {
      title: '№',
      dataIndex: 'id',
      sorter: true,
      width: 50
    },
    {
      title: 'Начало',
      key: 'startDate',
      width: '130px',
      render: (record: any) =>
        formatDateTimeToString(record.startDate, 'DD.MM.YYYY')
    },
    {
      title: 'Окончание',
      key: 'endDate',
      width: '130px',
      render: (record: any) =>
        formatDateTimeToString(record.endDate, 'DD.MM.YYYY')
    },
    {
      title: 'КБМ',
      dataIndex: 'kbm',
      sorter: true,
      width: 50
    },
    {
      title: 'КО',
      dataIndex: 'ko',
      sorter: true,
      width: 50
    },
    {
      title: 'КВС',
      dataIndex: 'kvc',
      sorter: true,
      width: 50
    },
    {
      title: 'КП',
      dataIndex: 'kp',
      sorter: true,
      width: 50
    },
    {
      title: 'КС',
      dataIndex: 'kc',
      sorter: true,
      width: 50
    },
    {
      title: 'КН',
      dataIndex: 'kn',
      sorter: true,
      width: 50
    }
  ];

  canAdd = () =>
    this.props.userAccess.some(access =>
      canAddOsagoMultiplier.includes(access)
    );

  onConfirmCheck = async () => {
    try {
      const fetchParams = {
        startDate: formatDateTimeToISOString({}, moment(this.state.startDate))
      };
      const res = await osagoMultiplier.fetchDateIntersectionMultiplier(
        'general',
        fetchParams
      );
      if (!res) {
        navigate(`/admin/osago-multiplier/new`, false, {
          state: fetchParams
        });
      } else {
        notification.warning({
          message: 'Ошибка',
          description: 'Выбранная дата уже входит в существующий диапазон!'
        });
      }
    } catch (error) {
      notification.warning({
        message: 'Ошибка',
        description: error.message
      });
    }
  };

  applyFilter = (filter: GenerelFilterParams) => {
    const { page } = this.state;
    this.setState({ filter }, () => this.fetchOsagoGeneralMultiplier(page));
  };

  cleanFilter = () => {
    const { page } = this.state;
    this.setState({ filter: {} }, () => this.fetchOsagoGeneralMultiplier(page));
  };

  render() {
    const { location } = this.props;
    const {
      data,
      totalCount,
      pageSize,
      page,
      loading,
      startDate,
      filter
    } = this.state;
    const canAdd = this.canAdd();
    return (
      <>
        <Header
          left={<h1>Общие коэффициенты ОСАГО</h1>}
          right={
            canAdd && (
              <Popconfirm
                title={
                  <>
                    <p>Дата начала действия</p>
                    <DatePicker
                      format="DD.MM.YYYY"
                      value={startDate}
                      onChange={(value: moment, dateString: string) => {
                        this.setState({
                          startDate: value
                        });
                      }}
                    />
                  </>
                }
                onConfirm={this.onConfirmCheck}
              >
                <Link to="/admin/osago-multiplier/new">
                  <Button type="primary">Создать</Button>
                </Link>
              </Popconfirm>
            )
          }
        />
        <HeaderTabs selectedTab="general" />
        <Section>
          <Filter
            filter={filter}
            applyFilter={this.applyFilter}
            cleanFilter={this.cleanFilter}
          />
          <Table
            onRow={record => ({
              onClick: () =>
                canAdd
                  ? navigate(
                      `/admin/osago-multiplier/edit/${record.id}`
                    )
                  : null
            })}
            columns={this.columns}
            fetch={this.fetchOsagoGeneralMultiplier}
            loading={loading}
            pagination={{
              page,
              pageSize,
              totalCount,
              location
            }}
            data={data}
          />
        </Section>
      </>
    );
  }
}

export default withUserAccess(OsagoGeneralMultiplierForm);
